import React from "react";
import UnauthorisedPage from "../Unauthorised";
import AdminSideNav from "../../common/AdminSideNav.js";
import AnalystSideNav from "../../common/AnalystSideNav.js";
import TopNav from "../../common/TopNav.js";
import { connect } from "react-redux";
import requireAuth from "../../hoc/requireAuth";
import authorizeAnalystOrAdmin from "../../hoc/authorizeAnalystOrAdmin";
// Bootstrap CSS
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/js/src/collapse.js";
import "../../assets/css/domain-search-page.css";
import "../../assets/css/table.css";
import Swal from "sweetalert2/dist/sweetalert2.js";
import "sweetalert2/src/sweetalert2.scss";
import download from "downloadjs";
import { Table } from "react-bootstrap";
import {
  castToDefaultDateFormat,
  castToTimeFormat,
} from "../../settings/index";

const MATCH_TYPES = ["Starts with", "Ends with", "Contains", "Exact match"];
const DEFAULT_MATCH_TYPE = "Contains";

// each keyword is searched on its own, so cost is per keyword, not per search
const CREDITS_PER_KEYWORD = { domains: 1, subdomains: 10 };

const SEARCH_TYPES = [
  { value: "domains", label: "Domains only (1 DRS credit per keyword)" },
  {
    value: "subdomains",
    label: "Subdomains only (10 DRS credits per keyword)",
  },
  { value: "both", label: "Domains and subdomains" },
];

class DomainDiscoveryPage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      authorised: true,
      searchType: SEARCH_TYPES[0].value,
      domainsInclude: "",
      domainsIncludeMatchType: DEFAULT_MATCH_TYPE,
      domainsExclude: "",
      domainsExcludeMatchType: DEFAULT_MATCH_TYPE,
      subdomainsInclude: "",
      subdomainsIncludeMatchType: DEFAULT_MATCH_TYPE,
      subdomainsExclude: "",
      subdomainsExcludeMatchType: DEFAULT_MATCH_TYPE,
      sinceDate: "",
      fetchAll: true,
      screenshot: true,
      isSearching: false,
      downloadingId: null,
      queues: [],
      isLoadingList: false,
      balance: null,
      errorMessage: {},
    };
  }

  componentDidMount() {
    this.fetchBalance();
    this.listRecords();
  }

  async listRecords() {
    this.setState({ isLoadingList: true });

    try {
      const res = await window.axios.get("/queue/getRecords", {
        params: { report: "domainDiscovery", page: 1, perPage: 10 },
      });
      this.setState({ queues: res.data.docs || [] });
    } catch (error) {
      console.error("Error fetching discovery queue:", error);
    } finally {
      this.setState({ isLoadingList: false });
    }
  }

  toTermsArray(value) {
    return value
      .split(/[\n,]/)
      .map((t) => t.trim())
      .filter(Boolean);
  }

  buildTermsBlock(prefix) {
    const include = this.toTermsArray(this.state[`${prefix}Include`]);
    if (!include.length) return null;

    const block = {
      include,
      includeMatchType: this.state[`${prefix}IncludeMatchType`],
    };

    const exclude = this.toTermsArray(this.state[`${prefix}Exclude`]);
    if (exclude.length) {
      block.exclude = exclude;
      block.excludeMatchType = this.state[`${prefix}ExcludeMatchType`];
    }

    return block;
  }

  async fetchBalance() {
    await window.axios
      .get("/domain-discovery/balance")
      .then((res) => {
        this.setState({ balance: res.data });
      })
      .catch(() => {
        this.setState({ balance: null });
      });
  }

  renderBalance() {
    const { balance } = this.state;
    if (!balance)
      return <span className="text-muted">Balance unavailable</span>;

    const items = balance.data || balance.result || [];
    const drs = Array.isArray(items)
      ? items.find((item) =>
          /domain research|drs/i.test(
            item?.product?.name || item?.productName || "",
          ),
        )
      : null;
    const credits = drs?.credits ?? drs?.balance;

    if (credits === undefined)
      return <span className="text-muted">Balance unavailable</span>;

    return (
      <span>
        <strong>DRS credits available:</strong> {credits}
      </span>
    );
  }

  showDomains() {
    return (
      this.state.searchType === "domains" || this.state.searchType === "both"
    );
  }

  showSubdomains() {
    return (
      this.state.searchType === "subdomains" || this.state.searchType === "both"
    );
  }

  keywordCount(prefix) {
    return this.toTermsArray(this.state[`${prefix}Include`]).length;
  }

  // every keyword becomes its own single-block request, so domain and subdomain
  // keywords are billed separately at their own rates and never as a combined search
  estimatedCredits() {
    const domains = this.showDomains() ? this.keywordCount("domains") : 0;
    const subdomains = this.showSubdomains()
      ? this.keywordCount("subdomains")
      : 0;

    return (
      domains * CREDITS_PER_KEYWORD.domains +
      subdomains * CREDITS_PER_KEYWORD.subdomains
    );
  }

  // searches run in the background: this only queues the job,
  // the report is downloaded later from /report/:id once the cron has processed it
  async queueSearch() {
    const { sinceDate, fetchAll, screenshot } = this.state;

    const reqData = { fetchAll, screenshot };

    // only send the blocks the selected search type asks for,
    // since extra blocks cost extra credits
    const domains = this.showDomains() ? this.buildTermsBlock("domains") : null;
    const subdomains = this.showSubdomains()
      ? this.buildTermsBlock("subdomains")
      : null;

    if (domains) reqData.domains = domains;
    if (subdomains) reqData.subdomains = subdomains;
    if (sinceDate) reqData.sinceDate = sinceDate;

    if (!domains && !subdomains) {
      Swal.fire(
        "Oops...",
        "Add at least one domain or subdomain search term.",
        "error",
      );
      return;
    }

    this.setState({ isSearching: true, errorMessage: {} });

    try {
      await window.axios.post("/domain-discovery/search", reqData);

      this.listRecords();
      Swal.fire(
        "Queued",
        "Search queued. The report becomes available once processing completes (checked every few minutes).",
        "success",
      );
    } catch (error) {
      const errorData = error?.response?.data;

      if (error?.response?.status === 422 && errorData?.errors) {
        this.setState({ errorMessage: errorData.errors });
      }

      Swal.fire(
        "Oops...",
        errorData?.message || "Domain discovery failed.",
        "error",
      );
    } finally {
      this.setState({ isSearching: false });
    }
  }

  async downloadReport(queue) {
    this.setState({ downloadingId: queue._id });

    try {
      const response = await window.axios.get(
        `/domain-discovery/report/${queue._id}`,
        {
          responseType: "blob",
          timeout: 300000,
        },
      );

      const mimeType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const filename = `domain-discovery-${Date.now()}.xlsx`;
      download(response.data, filename, mimeType);
    } catch (error) {
      let errorData = error?.response?.data;

      // blob responses need decoding before we can read the error json
      if (errorData instanceof Blob) {
        try {
          errorData = JSON.parse(await errorData.text());
        } catch (e) {
          errorData = null;
        }
      }

      // 409 carries the job's current status: Pending / Processing / Failed
      if (error?.response?.status === 409 && errorData?.status) {
        this.listRecords();

        if (errorData.status === "Failed") {
          Swal.fire("Oops...", "The search failed. Queue a new one.", "error");
        } else {
          Swal.fire(
            "Not ready",
            `Report is still ${errorData.status.toLowerCase()}. Try again in a few minutes.`,
            "info",
          );
        }
        return;
      }

      Swal.fire(
        "Oops...",
        errorData?.message || "Report download failed.",
        "error",
      );
    } finally {
      this.setState({ downloadingId: null });
    }
  }

  renderQueueList() {
    const { queues, isLoadingList, downloadingId } = this.state;

    return (
      <div className="row">
        <div className="col-12">
          <div className="card card-small mb-4">
            <div className="card-body p-0">
              <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
                <h6 className="mb-0">Queued Searches</h6>
                <button
                  onClick={() => this.listRecords()}
                  disabled={isLoadingList}
                  className="btn btn-sm btn-outline-secondary"
                >
                  {isLoadingList ? "Refreshing..." : "Refresh"}
                </button>
              </div>
              <Table hover className="table mb-0">
                <thead className="bg-light uppercase-th">
                  <tr>
                    <th width="5%">Sr.</th>
                    <th width="20%">Date & Time added</th>
                    <th width="20%">Username</th>
                    <th width="15%">No of Records</th>
                    <th width="15%">Status</th>
                    <th width="15%">Download</th>
                  </tr>
                </thead>
                <tbody>
                  {queues.length === 0 && (
                    <tr>
                      <td colSpan="6" className="text-center text-muted">
                        {isLoadingList
                          ? "Loading..."
                          : "No discovery searches queued yet."}
                      </td>
                    </tr>
                  )}
                  {queues.map((queue, index) => (
                    <tr key={queue._id || index}>
                      <td>{index + 1}</td>
                      <td>
                        {queue.DateCreated &&
                          `${castToDefaultDateFormat(queue.DateCreated)} ${castToTimeFormat(queue.DateCreated)}`}
                      </td>
                      <td title={queue.username}>{queue.username}</td>
                      <td>{queue.numOfRecords ?? "-"}</td>
                      <td>
                        <span
                          className={`badge ${
                            queue.status === "Processed"
                              ? "bg-success"
                              : queue.status === "Pending"
                                ? "bg-warning text-dark"
                                : queue.status === "Failed"
                                  ? "bg-danger"
                                  : "bg-secondary"
                          }`}
                        >
                          {queue.status}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-ldotr"
                          disabled={
                            queue.status !== "Processed" ||
                            downloadingId === queue._id
                          }
                          onClick={() => this.downloadReport(queue)}
                        >
                          {downloadingId === queue._id
                            ? "Downloading..."
                            : "Download"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // a match type is meaningless with no keywords to apply it to
  renderMatchType(label, keywordsKey, matchTypeKey) {
    if (!this.toTermsArray(this.state[keywordsKey]).length) return null;

    return (
      <div className="form-group">
        <label>{label}</label>
        <select
          value={this.state[matchTypeKey]}
          onChange={(e) => this.setState({ [matchTypeKey]: e.target.value })}
          className="form-control"
        >
          {MATCH_TYPES.map((type, index) => (
            <option key={index} value={type}>
              {type}
            </option>
          ))}
        </select>
      </div>
    );
  }

  renderTermsSection(title, prefix) {
    const colClass =
      this.state.searchType === "both" ? "col-md-6 col-sm-12" : "col-12";
    const errorKey = title.toLowerCase();

    return (
      <div className={colClass}>
        <h6 className="border-bottom pb-2">{title}</h6>

        <div className="form-group">
          <label>
            Include keywords (new line or comma each, searched separately)
          </label>
          <textarea
            value={this.state[`${prefix}Include`]}
            onChange={(e) =>
              this.setState({ [`${prefix}Include`]: e.target.value })
            }
            rows={3}
            className="form-control"
          ></textarea>
          <span className="form-text-error-text">
            {this.state.errorMessage?.[`${errorKey}.include`]}
          </span>
        </div>
        {this.renderMatchType(
          "Include match type",
          `${prefix}Include`,
          `${prefix}IncludeMatchType`,
        )}

        <div className="form-group">
          <label>
            Exclude keywords (optional, max 4, applied to every search)
          </label>
          <textarea
            value={this.state[`${prefix}Exclude`]}
            onChange={(e) =>
              this.setState({ [`${prefix}Exclude`]: e.target.value })
            }
            rows={3}
            className="form-control"
          ></textarea>
          <span className="form-text-error-text">
            {this.state.errorMessage?.[`${errorKey}.exclude`]}
          </span>
        </div>
        {this.renderMatchType(
          "Exclude match type",
          `${prefix}Exclude`,
          `${prefix}ExcludeMatchType`,
        )}
      </div>
    );
  }

  renderHtml() {
    if (!this.state.authorised) {
      return <UnauthorisedPage />;
    }

    return (
      <div className="row">
        {this.props?.login?.user?.role === "Admin" ? (
          <AdminSideNav />
        ) : (
          <AnalystSideNav />
        )}

        <main className="main-content col-lg-10 col-md-9 col-sm-12 p-0 offset-lg-2 offset-md-3">
          <TopNav />

          <div className="main-content-container container-fluid px-4">
            <div className="page-header row no-gutters py-4">
              <div className="col-12">
                <div className="row">
                  <div className="col-6 text-left mb-0">
                    <span className="text-uppercase page-subtitle">
                      Domain Discovery
                    </span>
                  </div>
                  <div className="col-6 text-right mb-0">
                    <span className="page-subtitle">
                      {this.renderBalance()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                <div className="card card-small mb-4">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-4 col-sm-12">
                        <div className="form-group">
                          <label>Search for</label>
                          <select
                            value={this.state.searchType}
                            onChange={(e) =>
                              this.setState({ searchType: e.target.value })
                            }
                            className="form-control"
                          >
                            {SEARCH_TYPES.map((type, index) => (
                              <option key={index} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="col-md-4 col-sm-12">
                        <div className="form-group">
                          <label>Estimated cost</label>
                          <input
                            readOnly
                            value={`${this.estimatedCredits()} DRS credits`}
                            className="form-control-plaintext"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      {this.showDomains() &&
                        this.renderTermsSection("Domains", "domains")}
                      {this.showSubdomains() &&
                        this.renderTermsSection("Subdomains", "subdomains")}
                    </div>
                    <div className="row align-items-end mt-3">
                      <div className="col-md-3 col-sm-12">
                        <div className="form-group mb-0">
                          <label>Discovered since (optional)</label>
                          <input
                            type="date"
                            value={this.state.sinceDate}
                            onChange={(e) =>
                              this.setState({ sinceDate: e.target.value })
                            }
                            className="form-control"
                          />
                          <span className="form-text-error-text">
                            {this.state.errorMessage?.sinceDate}
                          </span>
                        </div>
                      </div>
                      <div className="col-md-2 col-sm-12">
                        <div className="form-group mb-2">
                          <label className="mb-0">
                            <input
                              type="checkbox"
                              checked={this.state.fetchAll}
                              onChange={(e) =>
                                this.setState({ fetchAll: e.target.checked })
                              }
                            />{" "}
                            Fetch all pages
                          </label>
                        </div>
                      </div>
                      <div className="col-md-3 col-sm-12">
                        <div className="form-group mb-2">
                          <label className="mb-0">
                            <input
                              type="checkbox"
                              checked={this.state.screenshot}
                              onChange={(e) =>
                                this.setState({ screenshot: e.target.checked })
                              }
                            />{" "}
                            Screenshot discovered domains
                          </label>
                        </div>
                      </div>
                      <div className="col-md-4 col-sm-12 text-right">
                        <button
                          onClick={() => this.queueSearch()}
                          disabled={this.state.isSearching}
                          className="btn btn-primary"
                        >
                          {this.state.isSearching
                            ? "Queueing..."
                            : "Queue Search"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {this.renderQueueList()}
          </div>
        </main>
      </div>
    );
  }

  render() {
    return <>{this.renderHtml()}</>;
  }
}

const mapStateToProps = (state) => ({
  login: state.login,
  response: state.response,
});

export default connect(mapStateToProps)(
  authorizeAnalystOrAdmin(requireAuth(DomainDiscoveryPage)),
);
